import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCommentContract } from '@modules/build-hub-comment/hooks/useCommentContract';
import type { Candidate } from '@modules/build-hub-comment/components/CommentAutoCompleter';
import { CommentAutoComplete } from '@modules/build-hub-comment/components/CommentAutoCompleter';
import { CommentSubmitPayload as Schema } from '@modules/build-hub-comment/types';
import type {
    CommentSubmitPayload,
    Entity,
} from '@modules/build-hub-comment/types';
import { Button } from '@rocco/components/button';

interface FormValues {
    documentId?: string;
    message: string;
}

export function CommentForm({
    documentId,
    onSubmit,
    candidates,
    autoFocus = false,
    disabled = false,
}: {
    documentId?: string;
    onSubmit: (payload: CommentSubmitPayload) => Promise<void> | void;
    candidates: Candidate[];
    autoFocus?: boolean;
    disabled?: boolean;
}) {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const { register, handleSubmit, reset, watch, getValues } =
        useForm<FormValues>({
            resolver: zodResolver(
                Schema.pick({ message: true, context: true })
            ),
            defaultValues: { documentId, message: '' },
        });

    const { encode } = useCommentContract();

    // Single source of truth: watch the form value
    const message = watch('message');
    const [query, setQuery] = React.useState<string | null>(null);
    const [entities, setEntities] = React.useState<Entity[]>([]);
    const [highlightIndex, setHighlightIndex] = React.useState<number>(0);
    const [triggerStart, setTriggerStart] = React.useState<number | null>(null);
    const [lineCount, setLineCount] = React.useState<number>(1);

    // Keep lineCount in sync with message
    React.useEffect(() => {
        const newLineCount = message ? message.split('\n').length : 1;
        setLineCount(newLineCount);
    }, [message]);

    // Auto-focus textarea when component mounts if autoFocus is true
    React.useEffect(() => {
        if (autoFocus && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [autoFocus]);

    const filteredCandidates = query
        ? candidates.filter((c) =>
              c.label.toLowerCase().includes(query.toLowerCase())
          )
        : candidates;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (query !== null) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightIndex((i) => (i + 1) % filteredCandidates.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightIndex(
                    (i) =>
                        (i - 1 + filteredCandidates.length) %
                        filteredCandidates.length
                );
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                const choice = filteredCandidates[highlightIndex];
                if (choice) {
                    if (choice.type === 'action' && choice.onAction) {
                        choice.onAction();
                        setQuery(null);
                        setTriggerStart(null);
                    } else {
                        handleSelect(choice);
                    }
                }
                return;
            }
            if (e.key === ' ') {
                setQuery(null);
                setTriggerStart(null);
            }
        } else {
            if (e.key === '@') {
                setTriggerStart(e.currentTarget.selectionStart); // mark '@' index
                setQuery('');
            }
            // Submit on Enter (without Shift), allow line break on Shift+Enter
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                // Use getValues to get the current form value synchronously
                const currentMessage = getValues('message');
                if (currentMessage?.trim()) {
                    void submitNow();
                }
            }
        }
    };

    const { onChange: registerOnChange, ...registerProps } =
        register('message');

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // Call react-hook-form's onChange to update form state
        void registerOnChange(e);

        const val = e.target.value;
        const cursor = e.target.selectionStart;

        if (triggerStart !== null) {
            if (cursor <= triggerStart) {
                // caret went back before '@', cancel mention
                setTriggerStart(null);
                setQuery(null);
                return;
            }
            const q = val.slice(triggerStart + 1, cursor);
            setQuery(q);
            setHighlightIndex(0);
        }
    };

    const handleSelect = (c: Candidate) => {
        const textarea = document.getElementById(
            'comment-msg'
        ) as HTMLTextAreaElement;
        if (!textarea || triggerStart === null) return;

        const liveValue = textarea.value; // 👈 always read the live message
        const caret = textarea.selectionStart;

        // replace substring from triggerStart .. caret with "@Label"
        const before = liveValue.slice(0, triggerStart);
        const after = liveValue.slice(caret);
        const insertion = `@${c.label} `;
        const newMessage = before + insertion + after;

        setEntities((prev) => [
            ...prev,
            { type: c.type, id: c.id, label: c.label } as Entity,
        ]);

        // reset form with new message
        reset({ documentId, message: newMessage });
        setQuery(null);
        setTriggerStart(null);

        // move caret after inserted text
        requestAnimationFrame(() => {
            textarea.selectionStart = textarea.selectionEnd =
                before.length + insertion.length;
            textarea.focus();
        });
    };

    const submitNow = handleSubmit(async (values) => {
        const payload = encode(values.message, entities, { documentId });
        await onSubmit(payload);
        reset({ documentId, message: '' });
        setEntities([]);
        setTriggerStart(null);
        setQuery(null);
        setLineCount(1);
    });

    return (
        <div className="relative grid rounded-lg border bg-gray-100 transition-colors focus-within:border-gray-100 focus-within:bg-gray-50">
            {query !== null && (
                <CommentAutoComplete
                    query={query}
                    candidates={filteredCandidates}
                    highlightIndex={highlightIndex}
                    onSelect={handleSelect}
                    onCancel={() => {
                        setQuery(null);
                        setTriggerStart(null);
                    }}
                />
            )}
            <div
                className={`flex ${lineCount === 1 ? 'items-center' : 'flex-col'} cursor-text p-4`}
                onClick={() => textareaRef.current?.focus()}
            >
                <textarea
                    id="comment-msg"
                    {...registerProps}
                    ref={(e) => {
                        registerProps.ref(e);
                        if (textareaRef.current !== e) {
                            (
                                textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>
                            ).current = e;
                        }
                    }}
                    className="flex-1 resize-none border bg-transparent outline-none"
                    placeholder="Add a comment… use @name to mention"
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    rows={lineCount}
                />

                <div
                    className={`flex justify-end gap-2 ${lineCount === 1 ? 'ml-2' : 'mt-2'}`}
                >
                    <Button
                        color="dark"
                        onClick={() => submitNow()}
                        disabled={disabled || !message?.trim()}
                    >
                        {disabled ? 'Posting...' : 'Comment'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
